<template>
	<v-table hover density="compact" style="background-color: rgba(41, 41, 47, 0)">
		<tbody>
			<!-- 法面高さ -->
			<tr>
				<td style="width: 50%">
					{{ $t("Slopeheight") }}
				</td>
				<td>
					<DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
						:disabled="!propertyData[moundTypeSettings].generateShelf || !withSlope" :min-value="0.001"
						:max-value="99.999" :decimal-length="3" @blur="updateHeightDbSide()"
						v-model="propertyData[moundTypeSettings].height" />
				</td>
			</tr>
			<!-- 法面勾配 -->
			<tr>
				<td>
					{{ $t("Slopegradient") }}
				</td>
				<td>
					<SlopeSettingInput variant="underlined" :suffix-to-display="getSuffix(fillSettingSlopeUnit.slopeGradient)"
						:is-editable="withSlope" :slope-value="propertyData[moundTypeSettings].moundSlope" :step="0.01"
						@blur="updateMoundSlopeDbSide()" v-model.number="propertyData[moundTypeSettings].moundSlope"
						:max-value="fillSettingSlopeUnit.slopeGradient === 'percent' ? 9999.99 : 999.99" :min-value="0.01"
						:decimal-length="2" :un-valid-zero="false"></SlopeSettingInput>
				</td>
			</tr>
			<!-- 小段あり -->
			<tr>
				<td>
					{{ $t("Thereisberm") }}
				</td>
				<td>
					<v-checkbox color="primary" :disabled="!withSlope" hide-details density="compact" class="ma-0"
						@change="emitUpdatedSettings()" v-model="propertyData[moundTypeSettings].generateShelf"></v-checkbox>
				</td>
			</tr>
			<!-- 小段幅 -->
			<tr>
				<td>
					{{ $t("Bermwidth") }}
				</td>
				<td>
					<DistanceSettingInput variant="underlined" hide-details density="compact" :step="0.001"
						:disabled="!propertyData[moundTypeSettings].generateShelf || !withSlope" :min-value="0.001"
						:max-value="99.999" :decimal-length="3" @blur="updateShelfWidthDbSide()"
						v-model="propertyData[moundTypeSettings].shelfWidth" />
				</td>
			</tr>
			<!-- 小段勾配 -->
			<tr>
				<td>
					{{ $t("Bermgradient") }}
				</td>
				<td>
					<SlopeSettingInput variant="underlined" :suffix-to-display="getSuffix(fillSettingSlopeUnit.bermGradient)"
						:is-editable="propertyData[moundTypeSettings].generateShelf && withSlope"
						:slope-value="propertyData[moundTypeSettings].shelfSlope" :step="0.01" @blur="updateShelfSlopeDbSide()"
						v-model.number="propertyData[moundTypeSettings].shelfSlope"
						:max-value="fillSettingSlopeUnit.bermGradient === 'percent' ? 9999.99 : 999.99"
						:min-value="fillSettingSlopeUnit.bermGradient === 'percent' ? 0 : 0.01" :decimal-length="2"
						:un-valid-zero="fillSettingSlopeUnit.bermGradient === 'percent'"></SlopeSettingInput>

				</td>
			</tr>
			<!-- 法面色 -->
			<tr>
				<td>
					{{ $t("SLOPE_COLOR") }}
				</td>
				<td>
					<v-menu :disabled="!withSlope">
						<template v-slot:activator="{ props }">
							<v-avatar :style="horizontalTerrainColorStyle" :color="propertyData[moundTypeSettings].color" size="20px"
								v-bind="props">
							</v-avatar>
						</template>
						<v-color-picker class="ma-0" hide-canvas hide-inputs hide-sliders show-swatches mode="hexa"
							v-model="propertyData[moundTypeSettings].color"
							@update:model-value="emitUpdatedSettings()"></v-color-picker>
					</v-menu>
				</td>
			</tr>
		</tbody>
	</v-table>
</template>

<script lang="ts">
import SlopeSettingInput from "./SlopeSettingInput.vue";
import NumericSettingInput from "./NumericSettingInput.vue";
import DistanceSettingInput from "./DistanceSettingInput.vue";
import rules from "@/utils/validation-rules.js";
import { UnitTypes } from "../../constant";
import type { PropType } from "vue";
import { useStore } from "vuex";

type MoundSettings = {
	height: number;
	moundSlope: number;
	shelfWidth: number;
	shelfSlope: number;
	generateShelf: boolean;
	color: string;
};

type MoundTypeSettingsKey = "cutSettings" | "fillSettings";

type FlatMoundProperty = {
	surfaceSettings: {
		withSlope: boolean;
	};
	cutSettings: MoundSettings;
	fillSettings: MoundSettings;
};

export default {
	name: "PropertyFlatMoundSettings",
	setup() {
		return {
			store: useStore(),
		};
	},
	components: { SlopeSettingInput, NumericSettingInput, DistanceSettingInput },
	props: {
		// structural: reactive() drops FlatgroundObject private members
		property: {
			type: Object as PropType<
				Pick<
					FlatgroundObject,
					"surfaceSettings" | "cutSettings" | "fillSettings"
				>
			>,
			required: true,
		},
		moundTypeSettings: { type: String, required: true },
	},
	methods: {
		getSuffix(unitType) {
			const unit = UnitTypes.find((type) => type.value === unitType);
			return unit.symbol;
		},
		updateHeightDbSide() {
			this.emitUpdatedSettings();
		},
		updateMoundSlopeDbSide() {
			this.emitUpdatedSettings();
		},
		updateShelfWidthDbSide() {
			this.emitUpdatedSettings();
		},
		updateShelfSlopeDbSide() {
			this.emitUpdatedSettings();
		},
		isNum(val) {
			return typeof val == "number";
		},
		emitUpdatedSettings() {
			this.$emit("settingsUpdated");
		},
	},
	data() {
		return {
			rules: {
				...rules,
				validName: (v) => !/[~@#$^*()+=[\]{}|\\,?:<>!'"/;₹€&`%]/.test(v),
			},
			propertyData: { ...this.property },
		};
	},
	computed: {
		/**
		 * 水平地形色のスタイル
		 */
		horizontalTerrainColorStyle() {
			// 編集不可のときに適用するCSS
			if (!this.withSlope) {
				return {
					opacity: 0.5,
					"pointer-events": "none",
				};
			}
			// 編集可のときに適用するCSS
			return {
				opacity: 1.0,
			};
		},
		withSlope() {
			return this.propertyData.surfaceSettings.withSlope;
		},
		fillSettingSlopeUnit() {
			return this.store.state.fillSettingSlopeUnit;
		},
	},
	watch: {
		propertyData: {
			deep: true,
			handler(newProperty, oldProperty) {
				if (newProperty !== oldProperty) {
					this.$emit("update:property", newProperty);
				}
			},
		},
	},
};
</script>